import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { ClipboardList, Building2, XCircle, Loader, Handshake, Phone, Mail } from 'lucide-react';

/**
 * 관리자 — A/S 재제작 상담·연결 관리 (/admin/as-remake)
 * - 치과 신청(asRemakeRequests) 목록: 치과·선택 기공소들·재제작 내용·상태
 * - 관리자가 선택된 기공소 중 1곳을 배정 → [연결] → 서버 CF(assignAsRemakeRequest) 호출 → connected
 *   · 실제 연결(connections/partnerships 생성)은 서버 CF에서 처리 (클라이언트 직접 쓰기 금지)
 * - 상태 전이(reviewing/rejected)는 updateDoc으로 처리
 */
function AdminAsRemakeRequests() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selectedReq, setSelectedReq] = useState(null);
    const [adminNote, setAdminNote] = useState('');
    const [assignLabId, setAssignLabId] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => { fetchRequests(); }, []);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            let snap;
            try {
                snap = await getDocs(query(collection(db, 'asRemakeRequests'), orderBy('createdAt', 'desc')));
            } catch (e) {
                // 인덱스/정렬 실패 시 정렬 없이 폴백
                snap = await getDocs(collection(db, 'asRemakeRequests'));
            }
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setRequests(data);
        } catch (e) {
            console.error('A/S 재제작 신청 로드 실패:', e);
        }
        setLoading(false);
    };

    const openDetail = (req) => {
        setSelectedReq(req);
        setAdminNote(req.adminNotes || '');
        // 이미 배정된 기공소 있으면 기본값, 없으면 선택 기공소 첫 번째
        setAssignLabId(req.assignedLabId || (req.selectedLabIds && req.selectedLabIds[0]) || '');
    };

    const updateStatus = async (reqId, newStatus) => {
        setBusy(true);
        try {
            await updateDoc(doc(db, 'asRemakeRequests', reqId), {
                status: newStatus,
                adminNotes: adminNote || '',
                updatedAt: serverTimestamp(),
            });
            setRequests(prev => prev.map(r => r.id === reqId ? { ...r, status: newStatus, adminNotes: adminNote } : r));
            setSelectedReq(null);
        } catch (e) {
            console.error('상태 변경 실패:', e);
            alert('상태 변경에 실패했습니다.');
        }
        setBusy(false);
    };

    const handleConnect = async () => {
        if (!selectedReq) return;
        if (!assignLabId) { alert('연결할 기공소를 선택하세요.'); return; }
        const idx = (selectedReq.selectedLabIds || []).indexOf(assignLabId);
        const assignedLabName = idx >= 0 ? (selectedReq.selectedLabNames || [])[idx] : '';
        if (!window.confirm(`${assignedLabName || '선택한 기공소'}와(과) 연결하시겠습니까?\n연결 시 거래처가 자동으로 연결됩니다.`)) return;
        setBusy(true);
        try {
            // 메모는 CF가 사용하지 않으므로 연결 전에 별도 저장 (관리자 update 허용)
            if ((adminNote || '') !== (selectedReq.adminNotes || '')) {
                try {
                    await updateDoc(doc(db, 'asRemakeRequests', selectedReq.id), {
                        adminNotes: adminNote || '', updatedAt: serverTimestamp(),
                    });
                } catch (e) { /* 메모 저장 실패는 연결을 막지 않음 */ }
            }
            const functions = getFunctions(undefined, 'asia-northeast3');
            // 서버 CF (functions/lib/as-remake.js:51) — 파라미터: requestId, assignedLabId
            const assign = httpsCallable(functions, 'assignAsRemakeRequest');
            await assign({
                requestId: selectedReq.id,
                assignedLabId: assignLabId,
            });
            setRequests(prev => prev.map(r => r.id === selectedReq.id
                ? { ...r, status: 'connected', assignedLabId: assignLabId, assignedLabName, adminNotes: adminNote }
                : r));
            setSelectedReq(null);
            alert('연결이 완료되었습니다.');
        } catch (e) {
            console.error('연결 실패:', e);
            alert('연결에 실패했습니다: ' + (e?.message || '서버 오류'));
        }
        setBusy(false);
    };

    const fmtDate = (ts) => {
        if (!ts) return '-';
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const statusLabel = { pending: '대기', reviewing: '상담중', assigned: '배정', connected: '연결완료', rejected: '반려', cancelled: '취소' };
    const statusColor = { pending: '#f59e0b', reviewing: '#3b82f6', assigned: '#7c3aed', connected: '#10b981', rejected: '#ef4444', cancelled: '#94a3b8' };
    const statusBg = { pending: '#fffbeb', reviewing: '#eff6ff', assigned: '#f5f3ff', connected: '#f0fdf4', rejected: '#fef2f2', cancelled: '#f8fafc' };

    const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
    const pendingCount = requests.filter(r => r.status === 'pending').length;

    if (loading) return <div style={S.loading}>로딩 중...</div>;

    return (
        <div style={S.container}>
            <div style={S.header}>
                <h1 style={S.title}><ClipboardList size={20} style={{ verticalAlign: -3, marginRight: 6 }} />A/S 재제작 상담·연결 관리</h1>
                {pendingCount > 0 && <span style={S.pendingBadge}>{pendingCount}건 대기중</span>}
            </div>

            <div style={S.filterRow}>
                {[['all', '전체'], ['pending', '대기'], ['reviewing', '상담중'], ['connected', '연결완료'], ['rejected', '반려']].map(([val, label]) => (
                    <button key={val} onClick={() => setFilter(val)}
                        style={{ ...S.filterBtn, ...(filter === val ? S.filterBtnActive : {}) }}>
                        {label} {val !== 'all' && <span style={S.filterCount}>{requests.filter(r => r.status === val).length}</span>}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div style={S.empty}><ClipboardList size={40} color="#d1d5db" /><p>신청이 없습니다.</p></div>
            ) : (
                <div style={S.tableWrap}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                <th style={S.th}>상태</th>
                                <th style={S.th}>치과</th>
                                <th style={S.th}>선택 기공소</th>
                                <th style={S.th}>재제작 내용</th>
                                <th style={S.th}>배정 기공소</th>
                                <th style={S.th}>접수일</th>
                                <th style={S.th}>처리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((req, i) => (
                                <tr key={req.id} style={{ ...S.tr, backgroundColor: i % 2 ? '#f8fafc' : '#fff' }}>
                                    <td style={S.td}>
                                        <span style={{ ...S.statusBadge, backgroundColor: statusBg[req.status] || '#f1f5f9', color: statusColor[req.status] || '#64748b' }}>
                                            {statusLabel[req.status] || req.status}
                                        </span>
                                    </td>
                                    <td style={S.td}>
                                        <div style={S.cellName}>{req.clinicName || '-'}</div>
                                        {req.clinicPhone && <div style={S.cellSub}>{req.clinicPhone}</div>}
                                    </td>
                                    <td style={{ ...S.td, maxWidth: 200 }}>
                                        <div style={S.cellSub}>{(req.selectedLabNames || []).join(', ') || '-'}</div>
                                        <div style={{ fontSize: 11, color: '#c4b5fd' }}>{(req.selectedLabIds || []).length}곳</div>
                                    </td>
                                    <td style={{ ...S.td, maxWidth: 220 }}>
                                        <div style={S.cellName}>{req.remakeInfo?.prosthesisType || '-'} · {req.remakeInfo?.remakeReason || ''}</div>
                                        <div style={S.cellSub}>
                                            {req.remakeInfo?.teeth ? `치식 ${req.remakeInfo.teeth}` : ''}
                                            {req.remakeInfo?.shade ? ` · ${req.remakeInfo.shade}` : ''}
                                        </div>
                                    </td>
                                    <td style={S.td}>
                                        {req.assignedLabName
                                            ? <span style={{ color: '#059669', fontWeight: 700, fontSize: 12 }}><Handshake size={12} style={{ verticalAlign: -2 }} /> {req.assignedLabName}</span>
                                            : <span style={{ color: '#cbd5e1' }}>-</span>}
                                    </td>
                                    <td style={S.td}>{fmtDate(req.createdAt)}</td>
                                    <td style={S.td}>
                                        <button onClick={() => openDetail(req)} style={S.detailBtn}>상세·연결</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 상세 모달 */}
            {selectedReq && (
                <div style={S.overlay} onClick={() => setSelectedReq(null)}>
                    <div style={S.modal} onClick={e => e.stopPropagation()}>
                        <h2 style={S.modalTitle}>A/S 재제작 신청 상세</h2>

                        {/* 치과 정보 */}
                        <div style={S.infoCard}>
                            <h4 style={S.infoTitle}><Building2 size={16} color="#3b82f6" /> 신청 치과</h4>
                            <div style={S.infoGrid}>
                                <div style={S.infoItem}><span style={S.infoLabel}>치과명</span><span>{selectedReq.clinicName || '-'}</span></div>
                                <div style={S.infoItem}><span style={S.infoLabel}>전화</span>
                                    {selectedReq.clinicPhone ? <a href={`tel:${selectedReq.clinicPhone}`} style={S.phoneLink}><Phone size={12} /> {selectedReq.clinicPhone}</a> : '-'}
                                </div>
                                <div style={S.infoItem}><span style={S.infoLabel}>이메일</span>
                                    {selectedReq.clinicEmail ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Mail size={12} /> {selectedReq.clinicEmail}</span> : '-'}
                                </div>
                            </div>
                        </div>

                        {/* 재제작 내용 */}
                        <div style={S.infoCard}>
                            <h4 style={S.infoTitle}><ClipboardList size={16} color="#f59e0b" /> 재제작 내용</h4>
                            <div style={S.infoGrid}>
                                <div style={S.infoItem}><span style={S.infoLabel}>원 보철</span><span>{selectedReq.remakeInfo?.prosthesisType || '-'}</span></div>
                                <div style={S.infoItem}><span style={S.infoLabel}>사유</span><span>{selectedReq.remakeInfo?.remakeReason || '-'}</span></div>
                                <div style={S.infoItem}><span style={S.infoLabel}>치식</span><span>{selectedReq.remakeInfo?.teeth || '-'}</span></div>
                                <div style={S.infoItem}><span style={S.infoLabel}>쉐이드</span><span>{selectedReq.remakeInfo?.shade || '-'}</span></div>
                                <div style={S.infoItem}><span style={S.infoLabel}>케이스번호</span><span>{selectedReq.remakeInfo?.caseRef || '-'}</span></div>
                            </div>
                            {selectedReq.remakeInfo?.notes && <p style={S.modalMsg}>{selectedReq.remakeInfo.notes}</p>}
                        </div>

                        {/* 선택 기공소 + 배정 */}
                        <div style={S.infoCard}>
                            <h4 style={S.infoTitle}><Building2 size={16} color="#6366f1" /> 선택 기공소 중 1곳 배정</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {(selectedReq.selectedLabIds || []).map((labId, idx) => {
                                    const name = (selectedReq.selectedLabNames || [])[idx] || labId;
                                    const on = assignLabId === labId;
                                    const disabled = selectedReq.status === 'connected';
                                    return (
                                        <label key={labId} style={{ ...S.labChoice, ...(on ? S.labChoiceOn : {}), ...(disabled ? { opacity: 0.6, cursor: 'default' } : {}) }}>
                                            <input type="radio" name="assignLab" value={labId} checked={on} disabled={disabled}
                                                onChange={() => setAssignLabId(labId)} style={{ accentColor: '#059669' }} />
                                            <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>{name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 관리자 메모 */}
                        <div style={S.infoCard}>
                            <h4 style={S.infoTitle}>관리자 메모</h4>
                            <textarea value={adminNote} onChange={e => setAdminNote(e.target.value)}
                                placeholder="협의 내용을 기록하세요..." rows={3} style={S.textarea} />
                        </div>

                        {/* 액션 */}
                        <div style={S.actionRow}>
                            {selectedReq.status !== 'connected' && (
                                <button onClick={handleConnect} disabled={busy}
                                    style={{ ...S.actionBtn, backgroundColor: '#059669', color: '#fff' }}>
                                    <Handshake size={14} /> {busy ? '처리 중...' : '연결'}
                                </button>
                            )}
                            <button onClick={() => updateStatus(selectedReq.id, 'reviewing')} disabled={busy}
                                style={{ ...S.actionBtn, backgroundColor: '#3b82f6', color: '#fff' }}>
                                <Loader size={14} /> 상담중
                            </button>
                            <button onClick={() => updateStatus(selectedReq.id, 'rejected')} disabled={busy}
                                style={{ ...S.actionBtn, backgroundColor: '#ef4444', color: '#fff' }}>
                                <XCircle size={14} /> 반려
                            </button>
                            <button onClick={() => setSelectedReq(null)} style={{ ...S.actionBtn, backgroundColor: '#f1f5f9', color: '#64748b' }}>
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    container: { padding: '20px', maxWidth: 1200, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px 0', color: '#94a3b8' },
    header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
    title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' },
    pendingBadge: { padding: '4px 12px', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: 20, fontSize: 13, fontWeight: 600 },
    filterRow: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' },
    filterBtn: { padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 8, backgroundColor: '#fff', fontSize: 13, fontWeight: 600, color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
    filterBtnActive: { backgroundColor: '#059669', color: '#fff', borderColor: '#059669' },
    filterCount: { fontSize: 11, opacity: 0.7 },
    empty: { textAlign: 'center', padding: '60px 0', color: '#94a3b8' },
    tableWrap: { backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '10px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', backgroundColor: '#f8fafc' },
    tr: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: '10px 10px', fontSize: 13, color: '#334155', verticalAlign: 'top' },
    statusBadge: { padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
    cellName: { fontWeight: 600, color: '#0f172a', fontSize: 13 },
    cellSub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
    phoneLink: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#3b82f6', textDecoration: 'none', fontSize: 13, fontWeight: 500 },
    detailBtn: { padding: '5px 12px', backgroundColor: '#059669', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
    modal: { backgroundColor: '#fff', borderRadius: 16, padding: 24, maxWidth: 600, width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
    modalTitle: { margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#0f172a' },
    infoCard: { padding: 16, backgroundColor: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 12 },
    infoTitle: { display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#0f172a' },
    infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
    infoItem: { display: 'flex', flexDirection: 'column', gap: 2 },
    infoLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 600 },
    modalMsg: { margin: '10px 0 0', fontSize: 14, color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
    labChoice: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff' },
    labChoiceOn: { borderColor: '#059669', background: '#f0fdf4' },
    textarea: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
    actionRow: { display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' },
    actionBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

export default AdminAsRemakeRequests;
